const API_BASE = import.meta.env.VITE_PORTAL_API_BASE || import.meta.env.VITE_API_BASE || '/api/v1';
const TOKEN_KEY = 'fapay_portal_token';
const SESSION_KEY = 'fapay_portal_session';
const LEGACY_TOKEN_KEY = 'cnpay_portal_token';
const LEGACY_SESSION_KEY = 'cnpay_portal_session';

export interface PortalSession {
  token: string;
  expireAt: number;
  merchantNo: string;
  appId: string;
  appName: string;
}

export interface CaptchaData {
  key: string;
  img: string;
}

export interface PortalSiteConfig {
  platformName: string;
}

export interface PortalProfile {
  merchantNo: string;
  name: string;
  contactName: string;
  contactPhone: string;
  notifyUrl: string;
  returnUrl: string;
  appId: string;
  appName: string;
  ipWhitelist: string;
  secretViewAvailable: boolean;
  apiDomain: string;
}

export interface PortalProfileUpdate {
  contactName: string;
  contactPhone: string;
  notifyUrl: string;
  returnUrl: string;
  password?: string;
}

export interface PortalChannel {
  channelCode: string;
  name: string;
  payTypes: string;
  currency: string;
  feeRate: number;
}

export interface PortalOrder {
  id: number;
  tradeNo: string;
  merchantNo: string;
  appId: string;
  outTradeNo: string;
  channelCode: string;
  channelTradeNo: string;
  payType: string;
  subject: string;
  amount: number;
  fee: number;
  settleAmount: number;
  currency: string;
  status: string;
  payUrl?: string;
  qrCode?: string;
  notifyUrl?: string;
  returnUrl?: string;
  paidAt?: string;
  createdAt?: string;
}

export type PaymentAccountType = 'bank' | 'alipay' | 'wechat' | 'usdt';
export type PaymentAccountStatus = 'active' | 'disabled';

export interface PaymentAccount {
  id: number;
  merchantNo: string;
  type: PaymentAccountType;
  name: string;
  account: string;
  holder: string;
  extra?: string;
  isDefault: number | boolean;
  status: PaymentAccountStatus;
  createdAt?: string;
}

export interface WithdrawSummary {
  totalPaid: number;
  completedAmount: number;
  frozenAmount: number;
  availableAmount: number;
  currency: string;
  feeRate: number;
  minFee: number;
  minAmount: number;
  maxAmount: number;
  dailyLimit: number;
  todayCount: number;
}

export interface Withdrawal {
  id: number;
  withdrawalNo: string;
  merchantNo: string;
  accountId: number;
  accountType: string;
  accountName: string;
  accountNo: string;
  holderName: string;
  extra?: string;
  amount: number;
  fee: number;
  actualAmount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'canceled';
  remark?: string;
  failReason?: string;
  completedAt?: string;
  createdAt?: string;
}

export interface SettlementBatch {
  id: number;
  batchNo: string;
  sourceCurrency: string;
  periodStart?: string;
  periodEnd?: string;
  orderCount: number;
  grossAmount: number;
  feeAmount: number;
  refundAmount: number;
  netAmount: number;
  usdtPriceMicros: number;
  pricePointMicros: number;
  effectivePriceMicros: number;
  usdtAmountMinor: number;
  status: 'pending' | 'completed';
  transferTxid?: string;
  remark?: string;
  completedAt?: string;
  createdAt?: string;
}

export interface PortalDashboard {
  todayCount: number;
  todayAmount: number;
  todayGrossAmount: number;
  monthCount: number;
  monthAmount: number;
  monthGrossAmount: number;
  totalCount: number;
  totalAmount: number;
  totalGrossAmount: number;
  paidCount: number;
  pendingCount: number;
  failedCount: number;
  currency: string;
  currencyStats: PortalCurrencyStat[];
  recentOrders: PortalOrder[];
}

export interface PortalCurrencyStat {
  currency: string;
  todayCount: number;
  todayAmount: number;
  todayGrossAmount: number;
  monthCount: number;
  monthAmount: number;
  monthGrossAmount: number;
  totalCount: number;
  totalAmount: number;
  totalGrossAmount: number;
  paidCount: number;
  pendingCount: number;
  failedCount: number;
  availableAmount: number;
  frozenAmount: number;
  completedAmount: number;
}

export interface ListResponse<T> {
  channelList?: T[];
  orderList?: T[];
  withdrawalList?: T[];
  settlementList?: T[];
  total: number;
  currentPage: number;
}

function unwrap<T>(json: any): T {
  if (json?.code !== undefined && json.code !== 0 && json.code !== 200) {
    throw new Error(json.message || json.msg || '请求失败');
  }
  return (json?.data ?? json) as T;
}

export function getSession(): PortalSession | null {
  const raw = localStorage.getItem(SESSION_KEY) || localStorage.getItem(LEGACY_SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as PortalSession;
    if (!session.token || session.expireAt <= Math.floor(Date.now() / 1000)) {
      clearSession();
      return null;
    }
    if (!localStorage.getItem(SESSION_KEY)) {
      saveSession(session);
    }
    return session;
  } catch {
    clearSession();
    return null;
  }
}

export function saveSession(session: PortalSession) {
  localStorage.setItem(TOKEN_KEY, session.token);
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_SESSION_KEY);
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_SESSION_KEY);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const resp = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const text = await resp.text();
  const json = text ? JSON.parse(text) : {};
  if (!resp.ok) {
    throw new Error(json.message || json.msg || `HTTP ${resp.status}`);
  }
  return unwrap<T>(json);
}

export const portalApi = {
  siteConfig() {
    return request<PortalSiteConfig>('/portal/site-config', { method: 'GET' });
  },
  captcha() {
    return request<CaptchaData>('/pub/captcha/get', { method: 'GET' });
  },
  async login(merchantNo: string, password: string, verifyKey: string, verifyCode: string) {
    const session = await request<PortalSession>('/portal/login', {
      method: 'POST',
      body: JSON.stringify({ merchantNo, password, verifyKey, verifyCode }),
    });
    saveSession(session);
    return session;
  },
  profile() {
    return request<PortalProfile>('/portal/profile');
  },
  updateProfile(payload: PortalProfileUpdate) {
    return request('/portal/profile', { method: 'PUT', body: JSON.stringify(payload) });
  },
  channels(params: Record<string, string | number | undefined>) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') qs.set(key, String(value));
    });
    return request<ListResponse<PortalChannel>>(`/portal/channels?${qs.toString()}`);
  },
  apps() {
    return request<{ appList: PortalProfile[] }>('/portal/apps');
  },
  updateApp(payload: { appName: string; ipWhitelist: string }) {
    return request('/portal/app/config', { method: 'PUT', body: JSON.stringify(payload) });
  },
  revealAppSecret() {
    return request<{ appSecret: string }>('/portal/app/secret/reveal', { method: 'POST' });
  },
  dashboard() {
    return request<PortalDashboard>('/portal/dashboard');
  },
  orders(params: Record<string, string | number | undefined>) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') qs.set(key, String(value));
    });
    return request<ListResponse<PortalOrder>>(`/portal/orders?${qs.toString()}`);
  },
  paymentAccounts(type?: PaymentAccountType | 'all') {
    const qs = type && type !== 'all' ? `?type=${encodeURIComponent(type)}` : '';
    return request<{ accountList: PaymentAccount[] }>(`/portal/payment-accounts${qs}`);
  },
  addPaymentAccount(payload: Omit<PaymentAccount, 'id' | 'merchantNo' | 'status' | 'createdAt'>) {
    return request('/portal/payment-account', { method: 'POST', body: JSON.stringify(payload) });
  },
  updatePaymentAccount(id: number, payload: Pick<PaymentAccount, 'name' | 'account' | 'holder' | 'extra'>) {
    return request(`/portal/payment-account/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  },
  deletePaymentAccount(id: number) {
    return request(`/portal/payment-account/${id}`, { method: 'DELETE' });
  },
  setDefaultPaymentAccount(id: number) {
    return request(`/portal/payment-account/${id}/default`, { method: 'POST' });
  },
  updatePaymentAccountStatus(id: number, status: PaymentAccountStatus) {
    return request(`/portal/payment-account/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
  },
  withdrawSummary(currency?: string) {
    const qs = currency ? `?currency=${encodeURIComponent(currency)}` : '';
    return request<WithdrawSummary>(`/portal/withdraw/summary${qs}`);
  },
  withdrawals(params: Record<string, string | number | undefined>) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') qs.set(key, String(value));
    });
    return request<ListResponse<Withdrawal>>(`/portal/withdrawals?${qs.toString()}`);
  },
  settlements(params: Record<string, string | number | undefined>) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') qs.set(key, String(value));
    });
    return request<ListResponse<SettlementBatch>>(`/portal/settlements?${qs.toString()}`);
  },
  createWithdrawal(payload: { accountId: number; amount: number; currency: string; remark?: string }) {
    return request<{ withdrawalNo: string; amount: number; fee: number; actualAmount: number; status: string }>('/portal/withdrawal', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};
